import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    Compass,
    Plus,
    Target,
    TrendingUp,
    DollarSign,
    AlertTriangle,
    Lightbulb,
    ArrowRight,
    Settings,
    RefreshCw,
    BarChart3,
    Calendar,
    Sparkles,
    ChevronRight,
    Users,
    Eye,
    Wand2,
    FileText,
    Loader,
    CheckCircle,
    XCircle,
    Layers,
    ExternalLink,
    Archive,
    ArchiveRestore,
    ChevronDown,
    ChevronUp,
    SlidersHorizontal
} from 'lucide-react';
import { useApp } from '../contexts/AppContext';
import {
    getPillarsByBrand,
    getArchivedPillarsByBrand,
    savePillar,
    deletePillar,
    archivePillar,
    restorePillar,
    duplicatePillar,
    redistributeWeightsEvenly,
    updatePillarFrequency
} from '../services/pillarService';
import {
    getStrategyDashboardData,
    calculateRevenueProgress,
    updateBusinessGoals,
    completeStrategySetup
} from '../services/strategyService';
import PillarCard from '../components/strategy/PillarCard';
import PillarEditor from '../components/strategy/PillarEditor';
import PillarWeightsModal from '../components/strategy/PillarWeightsModal';
import ConfirmationModal from '../components/ConfirmationModal';
import StrategySetupWizard from '../components/strategy/StrategySetupWizard';
import { BulkContentPlanner } from '../components/bulk-planner';
import PromotionsCard from '../components/promotions/PromotionsCard';
import PromotionEditorModal from '../components/promotions/PromotionEditorModal';
import {
    getPromotionsByBrand,
    createPromotion,
    updatePromotion,
    deletePromotion as deletePromotionService
} from '../services/promotionService';
import ProductsCard from '../components/products/ProductsCard';
import ProductEditorModal from '../components/products/ProductEditorModal';
import DiscoverProductsModal from '../components/products/DiscoverProductsModal';
import BulkEditVisualNotesModal from '../components/products/BulkEditVisualNotesModal';
import {
    getProductsByBrand,
    createProduct,
    updateProduct,
    deleteProduct as deleteProductService,
    uploadProductImage
} from '../services/productService';
import { saveLibraryItem, itemExists } from '../services/libraryService';
import { useActiveBulkJobs, cancelJob } from '../hooks/useBulkGenerationJob';
import {
    getIncompletePlans,
    deleteContentPlan,
    archiveContentPlan,
    restoreContentPlan,
    getArchivedPlans,
    getCompletedPlans,
    checkPlanScheduledPosts,
    getPlanLibraryItems
} from '../services/contentPlanService';

/**
 * Strategy Dashboard Page
 * Primary entry point for marketing strategy management
 */
const StrategyDashboard = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { selectedBrand, user } = useApp();

    // Data states
    const [dashboardData, setDashboardData] = useState(null);
    const [pillars, setPillars] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Archived pillars
    const [archivedPillars, setArchivedPillars] = useState([]);
    const [showArchivedPillars, setShowArchivedPillars] = useState(false);

    // Weights modal
    const [showWeightsModal, setShowWeightsModal] = useState(false);
    const [savingWeights, setSavingWeights] = useState(false);

    // Modal states
    const [showPillarEditor, setShowPillarEditor] = useState(false);
    const [editingPillar, setEditingPillar] = useState(null);
    const [savingPillar, setSavingPillar] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [pillarToDelete, setPillarToDelete] = useState(null);
    const [showGoalsModal, setShowGoalsModal] = useState(false);

    // Goals form
    const [goalsForm, setGoalsForm] = useState({
        monthlyRevenueTarget: 0,
        averageOrderValue: 0,
        customerLifetimeValue: 0,
        currentChallenge: 'new_customers'
    });

    // Strategy Setup Wizard
    const [showSetupWizard, setShowSetupWizard] = useState(false);

    // Bulk Content Planner
    const [showBulkPlanner, setShowBulkPlanner] = useState(false);

    // Promotions
    const [promotions, setPromotions] = useState([]);
    const [promotionsLoading, setPromotionsLoading] = useState(false);
    const [showPromotionModal, setShowPromotionModal] = useState(false);
    const [editingPromotion, setEditingPromotion] = useState(null);
    const [savingPromotion, setSavingPromotion] = useState(false);
    const [showDeletePromotionConfirm, setShowDeletePromotionConfirm] = useState(false);
    const [promotionToDelete, setPromotionToDelete] = useState(null);

    // Products
    const [products, setProducts] = useState([]);
    const [productsLoading, setProductsLoading] = useState(false);
    const [showProductModal, setShowProductModal] = useState(false);
    const [editingProduct, setEditingProduct] = useState(null);
    const [savingProduct, setSavingProduct] = useState(false);
    const [showDeleteProductConfirm, setShowDeleteProductConfirm] = useState(false);
    const [productToDelete, setProductToDelete] = useState(null);
    const [showDiscoverModal, setShowDiscoverModal] = useState(false);
    const [showBulkEditVisualNotes, setShowBulkEditVisualNotes] = useState(false);
    const [bulkEditProducts, setBulkEditProducts] = useState([]);
    const [savingBulkEdit, setSavingBulkEdit] = useState(false);

    // Incomplete plans (for resuming)
    const [incompletePlans, setIncompletePlans] = useState([]);
    const [incompletePlansLoading, setIncompletePlansLoading] = useState(false);
    const [showDeletePlanConfirm, setShowDeletePlanConfirm] = useState(false);
    const [planToDelete, setPlanToDelete] = useState(null);
    const [deletePlanError, setDeletePlanError] = useState(null);
    const [deletingPlan, setDeletingPlan] = useState(false);
    const [planLibraryItemsCount, setPlanLibraryItemsCount] = useState(0);
    const [planScheduledPostsCount, setPlanScheduledPostsCount] = useState(0);
    const [deleteLibraryItems, setDeleteLibraryItems] = useState(true);

    // Archived and completed plans
    const [archivedPlans, setArchivedPlans] = useState([]);
    const [completedPlans, setCompletedPlans] = useState([]);
    const [showArchivedPlans, setShowArchivedPlans] = useState(false);
    const [showCompletedPlans, setShowCompletedPlans] = useState(false);
    const [archivedPlansLoading, setArchivedPlansLoading] = useState(false);
    const [completedPlansLoading, setCompletedPlansLoading] = useState(false);

    // Active background generation jobs
    const { activeJobs: backgroundJobs } = useActiveBulkJobs(user?.uid);
    const [resumePlanId, setResumePlanId] = useState(null);
    const [cancellingJobId, setCancellingJobId] = useState(null);

    // Check for resumePlanId in URL query params (from notification click)
    useEffect(() => {
        const urlResumePlanId = searchParams.get('resumePlanId');
        if (urlResumePlanId && !showBulkPlanner) {
            // Set the resume plan ID and open the bulk planner
            setResumePlanId(urlResumePlanId);
            setShowBulkPlanner(true);
            // Clear the URL param to prevent re-triggering on refresh
            searchParams.delete('resumePlanId');
            setSearchParams(searchParams, { replace: true });
        }
    }, [searchParams, setSearchParams, showBulkPlanner]);

    const handleCancelJob = async (jobId, e) => {
        e.stopPropagation();
        setCancellingJobId(jobId);
        try {
            await cancelJob(jobId);
        } catch (err) {
            console.error('Failed to cancel job:', err);
        } finally {
            setCancellingJobId(null);
        }
    };

    const handleOpenJobInPlanner = (job) => {
        if (job.planId) {
            setResumePlanId(job.planId);
            setShowBulkPlanner(true);
        }
    };

    // Load dashboard data
    useEffect(() => {
        if (selectedBrand?.id) {
            loadDashboardData();
        }
    }, [selectedBrand]);

    const loadDashboardData = async () => {
        if (!selectedBrand?.id) return;

        setLoading(true);
        setError(null);

        try {
            const data = await getStrategyDashboardData(selectedBrand.id);
            setDashboardData(data);
            // Deduplicate pillars by ID to prevent duplicate key warnings
            const uniquePillars = (data.pillars || []).filter((pillar, index, self) =>
                index === self.findIndex(p => p.id === pillar.id)
            );
            setPillars(uniquePillars);
            setGoalsForm(data.businessGoals || goalsForm);

            // Load archived pillars
            loadArchivedPillars();

            // Load promotions, products, and incomplete plans
            loadPromotions();
            loadProducts();
            loadIncompletePlans();
            loadArchivedPlans();
            loadCompletedPlans();
        } catch (err) {
            console.error('Error loading strategy dashboard:', err);
            setError('Failed to load strategy data');
        } finally {
            setLoading(false);
        }
    };

    const loadArchivedPillars = async () => {
        if (!selectedBrand?.id) return;
        try {
            const archived = await getArchivedPillarsByBrand(selectedBrand.id);
            // Deduplicate by ID to prevent duplicate key warnings
            const uniqueArchived = archived.filter((pillar, index, self) =>
                index === self.findIndex(p => p.id === pillar.id)
            );
            setArchivedPillars(uniqueArchived);
        } catch (err) {
            console.error('Error loading archived pillars:', err);
        }
    };

    const loadPromotions = async () => {
        if (!selectedBrand?.id) return;

        setPromotionsLoading(true);
        try {
            const promos = await getPromotionsByBrand(selectedBrand.id);
            setPromotions(promos);
        } catch (err) {
            console.error('Error loading promotions:', err);
        } finally {
            setPromotionsLoading(false);
        }
    };

    const loadProducts = async () => {
        if (!selectedBrand?.id) return;

        setProductsLoading(true);
        try {
            const prods = await getProductsByBrand(selectedBrand.id);
            setProducts(prods);
        } catch (err) {
            console.error('Error loading products:', err);
        } finally {
            setProductsLoading(false);
        }
    };

    const loadIncompletePlans = async () => {
        if (!selectedBrand?.id || !user?.uid) return;

        setIncompletePlansLoading(true);
        try {
            const plans = await getIncompletePlans(selectedBrand.id, user.uid);
            // Deduplicate by ID to prevent duplicate key warnings
            const uniquePlans = plans.filter((plan, index, self) =>
                index === self.findIndex(p => p.id === plan.id)
            );
            setIncompletePlans(uniquePlans);
        } catch (err) {
            console.error('Error loading incomplete plans:', err);
        } finally {
            setIncompletePlansLoading(false);
        }
    };

    const loadArchivedPlans = async () => {
        if (!selectedBrand?.id || !user?.uid) return;

        setArchivedPlansLoading(true);
        try {
            const plans = await getArchivedPlans(selectedBrand.id, user.uid);
            setArchivedPlans(plans);
        } catch (err) {
            console.error('Error loading archived plans:', err);
        } finally {
            setArchivedPlansLoading(false);
        }
    };

    const loadCompletedPlans = async () => {
        if (!selectedBrand?.id || !user?.uid) return;

        setCompletedPlansLoading(true);
        try {
            const plans = await getCompletedPlans(selectedBrand.id, user.uid);
            setCompletedPlans(plans);
        } catch (err) {
            console.error('Error loading completed plans:', err);
        } finally {
            setCompletedPlansLoading(false);
        }
    };

    const handleResumePlan = (planId) => {
        setResumePlanId(planId);
        setShowBulkPlanner(true);
    };

    const handleDeletePlan = async (plan) => {
        setPlanToDelete(plan);
        setDeletePlanError(null);
        setPlanLibraryItemsCount(0);
        setPlanScheduledPostsCount(0);
        setDeleteLibraryItems(true);

        // Check for library items and scheduled posts
        try {
            const [libraryResult, scheduledResult] = await Promise.all([
                getPlanLibraryItems(plan.id, user?.uid, selectedBrand?.id),
                checkPlanScheduledPosts(plan.id)
            ]);

            setPlanLibraryItemsCount(libraryResult.count);
            setPlanScheduledPostsCount(scheduledResult.scheduledCount);
        } catch (err) {
            console.error('Error checking plan dependencies:', err);
        }

        setShowDeletePlanConfirm(true);
    };

    const handleArchivePlan = async (plan) => {
        try {
            await archiveContentPlan(plan.id, user?.uid);
            loadIncompletePlans();
            loadCompletedPlans();
            loadArchivedPlans();
        } catch (err) {
            console.error('Error archiving plan:', err);
        }
    };

    const handleRestorePlan = async (plan) => {
        try {
            await restoreContentPlan(plan.id, user?.uid);
            loadArchivedPlans();
            loadIncompletePlans();
            loadCompletedPlans();
        } catch (err) {
            console.error('Error restoring plan:', err);
        }
    };

    const confirmDeletePlan = async () => {
        if (!planToDelete || deletingPlan) return;

        // Block deletion if there are scheduled posts
        if (planScheduledPostsCount > 0) {
            setDeletePlanError(`Cannot delete: ${planScheduledPostsCount} post(s) are still scheduled. Please cancel or publish them first.`);
            return;
        }

        setDeletePlanError(null);
        setDeletingPlan(true);

        // Debug logging
        console.log('Attempting to delete plan:', {
            planId: planToDelete.id,
            planUserId: planToDelete.userId,
            currentUserId: user?.uid,
            userIdMatch: planToDelete.userId === user?.uid,
            deleteLibraryItems,
            libraryItemsCount: planLibraryItemsCount
        });

        // Verify user ownership before attempting delete
        if (!user?.uid) {
            setDeletePlanError('You must be logged in to delete plans.');
            setDeletingPlan(false);
            return;
        }

        if (planToDelete.userId && planToDelete.userId !== user.uid) {
            console.error('User ID mismatch - cannot delete plan owned by another user');
            setDeletePlanError('You do not have permission to delete this plan. It was created by another user.');
            setDeletingPlan(false);
            return;
        }

        try {
            await deleteContentPlan(planToDelete.id, user.uid, {
                deleteLibraryItems: deleteLibraryItems && planLibraryItemsCount > 0,
                brandId: selectedBrand?.id
            });
            setShowDeletePlanConfirm(false);
            setPlanToDelete(null);
            setPlanLibraryItemsCount(0);
            setPlanScheduledPostsCount(0);
            loadIncompletePlans();
            loadCompletedPlans();
            loadArchivedPlans();
        } catch (err) {
            console.error('Error deleting plan:', err);
            // Show user-friendly error message
            if (err.code === 'permission-denied' || err.message?.includes('permissions')) {
                setDeletePlanError('You do not have permission to delete this plan. Please check that you are logged in correctly.');
            } else if (err.message?.includes('scheduled')) {
                setDeletePlanError(err.message);
            } else {
                setDeletePlanError(`Failed to delete plan: ${err.message}`);
            }
        } finally {
            setDeletingPlan(false);
        }
    };

    // Pillar actions
    const handleCreatePillar = () => {
        setEditingPillar(null);
        setShowPillarEditor(true);
    };

    const handleEditPillar = (pillar) => {
        setEditingPillar(pillar);
        setShowPillarEditor(true);
    };

    const handleSavePillar = async (pillarData) => {
        setSavingPillar(true);
        try {
            const isNew = !pillarData.id;
            await savePillar(pillarData, user?.uid, selectedBrand?.id);

            if (isNew && selectedBrand?.id) {
                // Redistribute weights evenly when creating a new pillar
                await redistributeWeightsEvenly(selectedBrand.id);
            } else if (!isNew && selectedBrand?.id) {
                // When editing, check if weight was manually changed
                const oldWeight = editingPillar?.frequency?.currentWeight || 0;
                const newWeight = pillarData.frequency?.currentWeight || 0;
                if (oldWeight !== newWeight) {
                    // Adjust other pillars so total remains 100%
                    const otherPillars = pillars.filter(p => p.id !== pillarData.id);
                    const remainingWeight = 100 - newWeight;
                    const otherTotal = otherPillars.reduce((sum, p) => sum + (p.frequency?.currentWeight || 0), 0);

                    if (otherPillars.length > 0 && otherTotal > 0) {
                        // Scale other pillars proportionally
                        const scale = remainingWeight / otherTotal;
                        let distributed = 0;
                        for (let i = 0; i < otherPillars.length; i++) {
                            const p = otherPillars[i];
                            let adjusted;
                            if (i === otherPillars.length - 1) {
                                // Last pillar gets whatever is left to avoid rounding issues
                                adjusted = remainingWeight - distributed;
                            } else {
                                adjusted = Math.round((p.frequency?.currentWeight || 0) * scale);
                                distributed += adjusted;
                            }
                            await savePillar(
                                { ...p, frequency: { ...p.frequency, currentWeight: Math.max(0, adjusted) } },
                                user?.uid,
                                selectedBrand?.id
                            );
                        }
                    } else if (otherPillars.length > 0) {
                        // All others were 0, distribute evenly
                        const baseWeight = Math.floor(remainingWeight / otherPillars.length);
                        const remainder = remainingWeight - (baseWeight * otherPillars.length);
                        for (let i = 0; i < otherPillars.length; i++) {
                            const p = otherPillars[i];
                            const w = baseWeight + (i < remainder ? 1 : 0);
                            await savePillar(
                                { ...p, frequency: { ...p.frequency, currentWeight: w } },
                                user?.uid,
                                selectedBrand?.id
                            );
                        }
                    }
                }
            }

            setShowPillarEditor(false);
            setEditingPillar(null);
            loadDashboardData();
        } catch (err) {
            console.error('Error saving pillar:', err);
        } finally {
            setSavingPillar(false);
        }
    };

    const handleDeletePillar = (pillar) => {
        setPillarToDelete(pillar);
        setShowDeleteConfirm(true);
    };

    const confirmDeletePillar = async () => {
        if (!pillarToDelete) return;

        try {
            await deletePillar(pillarToDelete.id);
            // Redistribute weights among remaining active pillars
            if (selectedBrand?.id) {
                await redistributeWeightsEvenly(selectedBrand.id);
            }
            setShowDeleteConfirm(false);
            setPillarToDelete(null);
            loadDashboardData();
        } catch (err) {
            console.error('Error deleting pillar:', err);
        }
    };

    const handleArchivePillar = async (pillar) => {
        try {
            if (pillar.isArchived) {
                await restorePillar(pillar.id);
                // Redistribute weights to include the restored pillar
                if (selectedBrand?.id) {
                    await redistributeWeightsEvenly(selectedBrand.id);
                }
            } else {
                await archivePillar(pillar.id);
                // Redistribute weights among remaining active pillars (exclude the archived one)
                if (selectedBrand?.id) {
                    await redistributeWeightsEvenly(selectedBrand.id, pillar.id);
                }
            }
            loadDashboardData();
        } catch (err) {
            console.error('Error archiving/restoring pillar:', err);
        }
    };

    const handleDuplicatePillar = async (pillar) => {
        try {
            await duplicatePillar(pillar.id, user?.uid, selectedBrand?.id);
            loadDashboardData();
        } catch (err) {
            console.error('Error duplicating pillar:', err);
        }
    };

    // Weights modal save
    const handleSaveWeights = async (weightEntries) => {
        setSavingWeights(true);
        try {
            for (const entry of weightEntries) {
                const pillar = pillars.find(p => p.id === entry.id);
                if (pillar) {
                    await updatePillarFrequency(entry.id, {
                        ...pillar.frequency,
                        currentWeight: entry.weight
                    });
                }
            }
            setShowWeightsModal(false);
            loadDashboardData();
        } catch (err) {
            console.error('Error saving pillar weights:', err);
        } finally {
            setSavingWeights(false);
        }
    };

    // Goals management
    const handleSaveGoals = async () => {
        try {
            await updateBusinessGoals(selectedBrand.id, goalsForm);
            if (!dashboardData?.setupComplete) {
                await completeStrategySetup(selectedBrand.id);
            }
            setShowGoalsModal(false);
            loadDashboardData();
        } catch (err) {
            console.error('Error saving goals:', err);
        }
    };

    // Promotion management
    const handleCreatePromotion = () => {
        setEditingPromotion(null);
        setShowPromotionModal(true);
    };

    const handleEditPromotion = (promotion) => {
        setEditingPromotion(promotion);
        setShowPromotionModal(true);
    };

    const handleSavePromotion = async (promotionData) => {
        setSavingPromotion(true);
        try {
            if (editingPromotion?.id) {
                await updatePromotion(editingPromotion.id, promotionData);
            } else {
                await createPromotion(selectedBrand.id, user?.uid, promotionData);
            }
            setShowPromotionModal(false);
            setEditingPromotion(null);
            loadPromotions();
        } catch (err) {
            console.error('Error saving promotion:', err);
        } finally {
            setSavingPromotion(false);
        }
    };

    const handleDeletePromotion = (promotion) => {
        setPromotionToDelete(promotion);
        setShowDeletePromotionConfirm(true);
    };

    const confirmDeletePromotion = async () => {
        if (!promotionToDelete) return;

        try {
            await deletePromotionService(promotionToDelete.id);
            setShowDeletePromotionConfirm(false);
            setPromotionToDelete(null);
            loadPromotions();
        } catch (err) {
            console.error('Error deleting promotion:', err);
        }
    };

    // Product management
    const handleCreateProduct = () => {
        setEditingProduct(null);
        setShowProductModal(true);
    };

    const handleEditProduct = (product) => {
        setEditingProduct(product);
        setShowProductModal(true);
    };

    const handleSaveProduct = async (productData) => {
        setSavingProduct(true);
        try {
            // Upload any base64 data URL images to Firebase Storage
            const processedImages = await Promise.all(
                (productData.images || []).map(async (img) => {
                    const imgUrl = typeof img === 'string' ? img : img.url;
                    const imgAlt = typeof img === 'string' ? '' : (img.alt || '');

                    if (imgUrl.startsWith('data:image')) {
                        const downloadURL = await uploadProductImage(
                            imgUrl,
                            user?.uid,
                            selectedBrand.id
                        );
                        return { url: downloadURL, alt: imgAlt };
                    }
                    return { url: imgUrl, alt: imgAlt };
                })
            );

            // Derive imageUrl from first image for backward compatibility
            const processedImageUrl = processedImages[0]?.url || '';

            const finalProductData = {
                ...productData,
                images: processedImages,
                imageUrl: processedImageUrl
            };

            // Save product images to asset library tagged as "product-image"
            const productName = productData.name || 'Product';
            const allImageUrls = new Set();
            for (const img of processedImages) {
                if (img.url) allImageUrls.add(img.url);
            }

            for (const imgUrl of allImageUrls) {
                try {
                    const alreadyInLibrary = await itemExists(user?.uid, selectedBrand.id, imgUrl);
                    if (!alreadyInLibrary) {
                        await saveLibraryItem({
                            title: productName,
                            name: productName,
                            type: 'image',
                            imageUrl: imgUrl,
                            tags: ['product-image'],
                        }, user?.uid, selectedBrand.id);
                    }
                } catch (err) {
                    console.error('Failed to save product image to library:', err);
                }
            }

            if (editingProduct?.id) {
                await updateProduct(editingProduct.id, finalProductData);
            } else {
                await createProduct(selectedBrand.id, user?.uid, finalProductData);
            }
            setShowProductModal(false);
            setEditingProduct(null);
            loadProducts();
        } catch (err) {
            console.error('Error saving product:', err);
        } finally {
            setSavingProduct(false);
        }
    };

    const handleDeleteProduct = (product) => {
        setProductToDelete(product);
        setShowDeleteProductConfirm(true);
    };

    const confirmDeleteProduct = async () => {
        if (!productToDelete) return;

        try {
            await deleteProductService(productToDelete.id);
            setShowDeleteProductConfirm(false);
            setProductToDelete(null);
            loadProducts();
        } catch (err) {
            console.error('Error deleting product:', err);
        }
    };

    const handleDiscoverProducts = () => {
        setShowDiscoverModal(true);
    };

    const handleImportProducts = async (productDataArray) => {
        setSavingProduct(true);
        try {
            for (const productData of productDataArray) {
                await createProduct(selectedBrand.id, user?.uid, productData);
            }
            loadProducts();
        } catch (err) {
            console.error('Error importing products:', err);
        } finally {
            setSavingProduct(false);
        }
    };

    const handleBulkEditVisualNotes = (selectedProducts) => {
        setBulkEditProducts(selectedProducts);
        setShowBulkEditVisualNotes(true);
    };

    const handleSaveBulkVisualNotes = async (visualNotes) => {
        setSavingBulkEdit(true);
        try {
            for (const product of bulkEditProducts) {
                await updateProduct(product.id, { visualNotes });
            }
            setShowBulkEditVisualNotes(false);
            setBulkEditProducts([]);
            loadProducts();
        } catch (err) {
            console.error('Error bulk updating visual notes:', err);
        } finally {
            setSavingBulkEdit(false);
        }
    };

    // Calculate revenue progress (mock data for now)
    const revenueProgress = calculateRevenueProgress(
        dashboardData?.currentRevenue || 0,
        dashboardData?.businessGoals?.monthlyRevenueTarget || 0
    );

    // Health score color
    const getHealthColor = (score) => {
        if (score >= 80) return 'var(--success-600)';
        if (score >= 60) return 'var(--warning-600)';
        if (score >= 40) return 'var(--warning-600)';
        return 'var(--error-600)';
    };

    if (!selectedBrand) {
        return (
            <div className="page-container" style={{ textAlign: 'center', paddingTop: 'var(--space-12)' }}>
                <Compass size={48} style={{ color: 'var(--color-text-tertiary)', marginBottom: 'var(--space-4)' }} />
                <h2>Select a Brand</h2>
                <p style={{ color: 'var(--color-text-secondary)' }}>
                    Choose a brand from the navigation to view your marketing strategy
                </p>
            </div>
        );
    }

    if (loading) {
        return (
            <>
                <div className="page-container" style={{ textAlign: 'center', paddingTop: 'var(--space-12)' }}>
                    <RefreshCw size={32} className="spin" style={{ color: 'var(--color-primary)', marginBottom: 'var(--space-4)' }} />
                    <p style={{ color: 'var(--color-text-secondary)' }}>Loading strategy dashboard...</p>
                </div>
                {/* Keep wizard mounted during loading to preserve its ref state */}
                <StrategySetupWizard
                    isOpen={showSetupWizard}
                    onClose={() => setShowSetupWizard(false)}
                    onComplete={() => {
                        setShowSetupWizard(false);
                        loadDashboardData();
                    }}
                />
            </>
        );
    }

    return (
        <div className="page-container">
            {/* Header */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 'var(--space-6)'
            }}>
                <div>
                    <h1 style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', margin: 0 }}>
                        <Compass size={28} />
                        Strategy Hub
                    </h1>
                    <p style={{ color: 'var(--color-text-secondary)', margin: 'var(--space-1) 0 0 0' }}>
                        Your marketing strategy for {selectedBrand.name}
                    </p>
                </div>
                <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                    {pillars.length === 0 && (
                        <button
                            onClick={() => setShowSetupWizard(true)}
                            className="btn btn-primary"
                        >
                            <Wand2 size={16} />
                            Setup Wizard
                        </button>
                    )}
                    <button
                        onClick={() => setShowGoalsModal(true)}
                        className="btn btn-ghost"
                    >
                        <Settings size={16} />
                        Goals
                    </button>
                    <button onClick={loadDashboardData} className="btn btn-ghost">
                        <RefreshCw size={16} />
                    </button>
                </div>
            </div>

            {/* Active Background Generation Jobs */}
            {backgroundJobs.length > 0 && (
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 'var(--space-2)',
                    marginBottom: 'var(--space-4)'
                }}>
                    {backgroundJobs.map(job => {
                        const isProcessing = job.status === 'pending' || job.status === 'processing';
                        const isCompleted = job.status === 'completed';
                        const isFailed = job.status === 'failed';
                        const progress = job.progress || {};
                        const results = job.results || {};
                        const progressPercent = progress.total > 0
                            ? Math.round((progress.current / progress.total) * 100)
                            : 0;

                        return (
                            <div
                                key={job.id}
                                className="card"
                                onClick={() => handleOpenJobInPlanner(job)}
                                style={{
                                    padding: 'var(--space-3) var(--space-4)',
                                    cursor: job.planId ? 'pointer' : 'default',
                                    background: isProcessing
                                        ? 'linear-gradient(135deg, var(--primary-50), var(--color-bg-primary))'
                                        : isCompleted
                                            ? 'linear-gradient(135deg, var(--success-50), var(--color-bg-primary))'
                                            : 'linear-gradient(135deg, var(--error-50), var(--color-bg-primary))',
                                    borderLeft: `4px solid ${isProcessing ? 'var(--color-primary)' : isCompleted ? 'var(--success-500)' : 'var(--error-500)'}`,
                                    transition: 'box-shadow var(--transition-fast)'
                                }}
                                onMouseEnter={(e) => { if (job.planId) e.currentTarget.style.boxShadow = 'var(--shadow-md)'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; }}
                            >
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: 'var(--space-3)'
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', flex: 1, minWidth: 0 }}>
                                        {isProcessing ? (
                                            <Loader size={18} className="spin" style={{ color: 'var(--color-primary)', flexShrink: 0 }} />
                                        ) : isCompleted ? (
                                            <CheckCircle size={18} style={{ color: 'var(--success-500)', flexShrink: 0 }} />
                                        ) : (
                                            <XCircle size={18} style={{ color: 'var(--error-500)', flexShrink: 0 }} />
                                        )}
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 'var(--space-2)',
                                                fontSize: 'var(--font-size-sm)',
                                                fontWeight: 600
                                            }}>
                                                <Layers size={14} />
                                                {isProcessing
                                                    ? `Generating creatives... ${progress.current || 0}/${progress.total || '?'}`
                                                    : isCompleted
                                                        ? `Generation complete - ${results.successCount || 0} creatives ready`
                                                        : 'Generation failed'}
                                            </div>
                                            {isProcessing && progress.currentBrief && (
                                                <div style={{
                                                    fontSize: 'var(--font-size-xs)',
                                                    color: 'var(--color-text-secondary)',
                                                    marginTop: '2px',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap'
                                                }}>
                                                    {progress.phase === 'waiting' ? 'Rate limit pause...' : progress.currentBrief}
                                                </div>
                                            )}
                                            {job.planId && (
                                                <div style={{
                                                    fontSize: 'var(--font-size-xs)',
                                                    color: 'var(--color-text-tertiary)',
                                                    marginTop: '2px'
                                                }}>
                                                    Click to view details
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', flexShrink: 0 }}>
                                        {isProcessing && (
                                            <>
                                                <span style={{
                                                    fontSize: 'var(--font-size-sm)',
                                                    fontWeight: 600,
                                                    color: 'var(--color-primary)'
                                                }}>
                                                    {progressPercent}%
                                                </span>
                                                <button
                                                    onClick={(e) => handleCancelJob(job.id, e)}
                                                    disabled={cancellingJobId === job.id}
                                                    className="btn btn-sm btn-ghost"
                                                    style={{ color: 'var(--error-600)', gap: 'var(--space-1)' }}
                                                    title="Cancel generation"
                                                >
                                                    <XCircle size={14} />
                                                    {cancellingJobId === job.id ? 'Cancelling...' : 'Cancel'}
                                                </button>
                                            </>
                                        )}
                                        {isCompleted && results.successCount > 0 && (
                                            <button
                                                onClick={(e) => { e.stopPropagation(); navigate(`/library?planId=${job.planId}`); }}
                                                className="btn btn-sm btn-primary"
                                                style={{ gap: 'var(--space-1)' }}
                                            >
                                                <ExternalLink size={12} />
                                                View in Library
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {/* Progress bar for processing jobs */}
                                {isProcessing && progress.total > 0 && (
                                    <div style={{
                                        marginTop: 'var(--space-2)',
                                        height: '4px',
                                        background: 'var(--color-bg-tertiary)',
                                        borderRadius: 'var(--radius-full)',
                                        overflow: 'hidden'
                                    }}>
                                        <div style={{
                                            width: `${progressPercent}%`,
                                            height: '100%',
                                            background: 'var(--color-primary)',
                                            borderRadius: 'var(--radius-full)',
                                            transition: 'width 0.5s ease'
                                        }} />
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Incomplete Content Plans - Resume Section */}
            {incompletePlans.length > 0 && (
                <div className="card" style={{
                    marginBottom: 'var(--space-4)',
                    background: 'linear-gradient(135deg, var(--warning-50), var(--color-bg-primary))',
                    borderLeft: '4px solid var(--warning-500)'
                }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: 'var(--space-3)'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                            <FileText size={18} style={{ color: 'var(--warning-600)' }} />
                            <span style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600 }}>
                                Continue Your Content Plans
                            </span>
                        </div>
                        <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)' }}>
                            {incompletePlans.length} incomplete plan{incompletePlans.length !== 1 ? 's' : ''}
                        </span>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                        {incompletePlans.slice(0, 3).map(plan => {
                            const progress = plan.progress || {};
                            const stepLabels = {
                                overview: 'Configure plan',
                                generate: 'Generate briefs',
                                review: 'Review briefs',
                                creatives: 'Generate creatives',
                                schedule: 'Schedule posts'
                            };
                            const progressPercent = progress.totalBriefs > 0
                                ? Math.round((progress.generatedBriefs / progress.totalBriefs) * 100)
                                : 0;

                            return (
                                <div
                                    key={plan.id}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        padding: 'var(--space-3)',
                                        background: 'var(--color-bg-primary)',
                                        borderRadius: 'var(--radius-md)',
                                        border: '1px solid var(--color-border)'
                                    }}
                                >
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{
                                            fontSize: 'var(--font-size-sm)',
                                            fontWeight: 500,
                                            marginBottom: '2px',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap'
                                        }}>
                                            {plan.name || 'Content Plan'}
                                        </div>
                                        <div style={{
                                            fontSize: 'var(--font-size-xs)',
                                            color: 'var(--color-text-secondary)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 'var(--space-2)'
                                        }}>
                                            <span>Next: {stepLabels[progress.resumeStep] || 'Continue'}</span>
                                            {progress.totalBriefs > 0 && (
                                                <>
                                                    <span style={{ color: 'var(--color-border)' }}>•</span>
                                                    <span>{progress.generatedBriefs}/{progress.totalBriefs} generated</span>
                                                </>
                                            )}
                                            {plan.updatedAt && (
                                                <>
                                                    <span style={{ color: 'var(--color-border)' }}>•</span>
                                                    <span>Updated {new Date(plan.updatedAt.seconds ? plan.updatedAt.seconds * 1000 : plan.updatedAt).toLocaleString()}</span>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                                        <button
                                            onClick={() => handleArchivePlan(plan)}
                                            className="btn btn-ghost btn-sm"
                                            style={{ color: 'var(--color-text-tertiary)', padding: 'var(--space-1)' }}
                                            title="Archive plan"
                                        >
                                            <Archive size={14} />
                                        </button>
                                        <button
                                            onClick={() => handleDeletePlan(plan)}
                                            className="btn btn-ghost btn-sm"
                                            style={{ color: 'var(--color-text-tertiary)', padding: 'var(--space-1)' }}
                                            title="Delete plan"
                                        >
                                            <XCircle size={14} />
                                        </button>
                                        <button
                                            onClick={() => handleResumePlan(plan.id)}
                                            className="btn btn-primary btn-sm"
                                        >
                                            Resume
                                            <ArrowRight size={14} />
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Completed Content Plans Section */}
            {completedPlans.length > 0 && (
                <div className="card" style={{
                    marginBottom: 'var(--space-4)',
                    background: 'linear-gradient(135deg, var(--success-50), var(--color-bg-primary))',
                    borderLeft: '4px solid var(--success-500)'
                }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: showCompletedPlans ? 'var(--space-3)' : 0,
                        cursor: 'pointer'
                    }}
                    onClick={() => setShowCompletedPlans(!showCompletedPlans)}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                            <CheckCircle size={18} style={{ color: 'var(--success-600)' }} />
                            <span style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600 }}>
                                Completed Plans
                            </span>
                            <span style={{
                                fontSize: 'var(--font-size-xs)',
                                color: 'var(--color-text-tertiary)',
                                background: 'var(--success-100)',
                                padding: '2px 8px',
                                borderRadius: 'var(--radius-full)'
                            }}>
                                {completedPlans.length}
                            </span>
                        </div>
                        {showCompletedPlans ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </div>
                    {showCompletedPlans && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                            {completedPlans.map(plan => (
                                <div
                                    key={plan.id}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        padding: 'var(--space-3)',
                                        background: 'var(--color-bg-primary)',
                                        borderRadius: 'var(--radius-md)',
                                        border: '1px solid var(--color-border)'
                                    }}
                                >
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{
                                            fontSize: 'var(--font-size-sm)',
                                            fontWeight: 500,
                                            marginBottom: '2px',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap'
                                        }}>
                                            {plan.name || 'Content Plan'}
                                        </div>
                                        <div style={{
                                            fontSize: 'var(--font-size-xs)',
                                            color: 'var(--color-text-secondary)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 'var(--space-2)'
                                        }}>
                                            <span style={{ color: 'var(--success-600)' }}>
                                                {plan.scheduledCount || 0} posts scheduled
                                            </span>
                                            {plan.updatedAt && (
                                                <>
                                                    <span style={{ color: 'var(--color-border)' }}>•</span>
                                                    <span>Completed {new Date(plan.updatedAt.seconds ? plan.updatedAt.seconds * 1000 : plan.updatedAt).toLocaleDateString()}</span>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                                        <button
                                            onClick={() => handleArchivePlan(plan)}
                                            className="btn btn-ghost btn-sm"
                                            style={{ color: 'var(--color-text-tertiary)', padding: 'var(--space-1)' }}
                                            title="Archive plan"
                                        >
                                            <Archive size={14} />
                                        </button>
                                        <button
                                            onClick={() => handleDeletePlan(plan)}
                                            className="btn btn-ghost btn-sm"
                                            style={{ color: 'var(--color-text-tertiary)', padding: 'var(--space-1)' }}
                                            title="Delete plan"
                                        >
                                            <XCircle size={14} />
                                        </button>
                                        <button
                                            onClick={() => handleResumePlan(plan.id)}
                                            className="btn btn-secondary btn-sm"
                                        >
                                            View
                                            <Eye size={14} />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Archived Content Plans Section */}
            {archivedPlans.length > 0 && (
                <div className="card" style={{
                    marginBottom: 'var(--space-4)',
                    background: 'var(--color-bg-secondary)',
                    borderLeft: '4px solid var(--color-text-tertiary)',
                    opacity: 0.9
                }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: showArchivedPlans ? 'var(--space-3)' : 0,
                        cursor: 'pointer'
                    }}
                    onClick={() => setShowArchivedPlans(!showArchivedPlans)}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                            <Archive size={18} style={{ color: 'var(--color-text-tertiary)' }} />
                            <span style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--color-text-secondary)' }}>
                                Archived Plans
                            </span>
                            <span style={{
                                fontSize: 'var(--font-size-xs)',
                                color: 'var(--color-text-tertiary)',
                                background: 'var(--color-bg-tertiary)',
                                padding: '2px 8px',
                                borderRadius: 'var(--radius-full)'
                            }}>
                                {archivedPlans.length}
                            </span>
                        </div>
                        {showArchivedPlans ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </div>
                    {showArchivedPlans && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                            {archivedPlans.map(plan => (
                                <div
                                    key={plan.id}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        padding: 'var(--space-3)',
                                        background: 'var(--color-bg-primary)',
                                        borderRadius: 'var(--radius-md)',
                                        border: '1px solid var(--color-border)'
                                    }}
                                >
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{
                                            fontSize: 'var(--font-size-sm)',
                                            fontWeight: 500,
                                            marginBottom: '2px',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            color: 'var(--color-text-secondary)'
                                        }}>
                                            {plan.name || 'Content Plan'}
                                        </div>
                                        <div style={{
                                            fontSize: 'var(--font-size-xs)',
                                            color: 'var(--color-text-tertiary)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 'var(--space-2)'
                                        }}>
                                            {plan.archivedAt && (
                                                <span>Archived {new Date(plan.archivedAt.seconds ? plan.archivedAt.seconds * 1000 : plan.archivedAt).toLocaleDateString()}</span>
                                            )}
                                        </div>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                                        <button
                                            onClick={() => handleRestorePlan(plan)}
                                            className="btn btn-ghost btn-sm"
                                            style={{ color: 'var(--color-text-secondary)', padding: 'var(--space-1)' }}
                                            title="Restore plan"
                                        >
                                            <ArchiveRestore size={14} />
                                        </button>
                                        <button
                                            onClick={() => handleDeletePlan(plan)}
                                            className="btn btn-ghost btn-sm"
                                            style={{ color: 'var(--error-500)', padding: 'var(--space-1)' }}
                                            title="Delete permanently"
                                        >
                                            <XCircle size={14} />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Revenue Progress Banner */}
            {dashboardData?.businessGoals?.monthlyRevenueTarget > 0 && (
                <div className="card" style={{
                    marginBottom: 'var(--space-6)',
                    background: 'linear-gradient(135deg, var(--color-bg-secondary), var(--color-bg-primary))'
                }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: 'var(--space-3)'
                    }}>
                        <div>
                            <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
                                Revenue Progress
                            </span>
                            <h2 style={{ margin: 'var(--space-1) 0', display: 'flex', alignItems: 'baseline', gap: 'var(--space-2)' }}>
                                <DollarSign size={24} style={{ color: 'var(--success-600)' }} />
                                ${(dashboardData?.currentRevenue || 0).toLocaleString()}
                                <span style={{ fontSize: 'var(--font-size-base)', color: 'var(--color-text-secondary)', fontWeight: 400 }}>
                                    / ${dashboardData.businessGoals.monthlyRevenueTarget.toLocaleString()} target
                                </span>
                            </h2>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                            <span style={{
                                fontSize: 'var(--font-size-2xl)',
                                fontWeight: 700,
                                color: revenueProgress.percentage >= 75 ? 'var(--success-600)' :
                                    revenueProgress.percentage >= 50 ? 'var(--warning-600)' : 'var(--error-600)'
                            }}>
                                {revenueProgress.percentage}%
                            </span>
                        </div>
                    </div>
                    <div style={{
                        height: '8px',
                        background: 'var(--color-bg-tertiary)',
                        borderRadius: 'var(--radius-full)',
                        overflow: 'hidden'
                    }}>
                        <div style={{
                            width: `${revenueProgress.percentage}%`,
                            height: '100%',
                            background: revenueProgress.percentage >= 75 ? 'var(--success-500)' :
                                revenueProgress.percentage >= 50 ? 'var(--warning-500)' : 'var(--error-500)',
                            borderRadius: 'var(--radius-full)',
                            transition: 'width var(--transition-normal)'
                        }} />
                    </div>
                    <div style={{
                        display: 'flex',
                        gap: 'var(--space-6)',
                        marginTop: 'var(--space-3)'
                    }}>
                        <div>
                            <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)' }}>AOV</span>
                            <p style={{ margin: 0, fontWeight: 500 }}>${dashboardData.businessGoals.averageOrderValue || 0}</p>
                        </div>
                        <div>
                            <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)' }}>Required Conversions</span>
                            <p style={{ margin: 0, fontWeight: 500 }}>{dashboardData.funnelTargets?.requiredConversions || 0}</p>
                        </div>
                        <div>
                            <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)' }}>Required Reach</span>
                            <p style={{ margin: 0, fontWeight: 500 }}>{(dashboardData.funnelTargets?.requiredReach || 0).toLocaleString()}</p>
                        </div>
                    </div>
                </div>
            )}

            {/* Main Grid */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(12, 1fr)',
                gap: 'var(--space-4)'
            }}>
                {/* Strategy Health */}
                <div className="card" style={{ gridColumn: 'span 3' }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--space-2)',
                        marginBottom: 'var(--space-3)'
                    }}>
                        <Target size={18} style={{ color: 'var(--color-text-secondary)' }} />
                        <span style={{ fontSize: 'var(--font-size-sm)', fontWeight: 500 }}>Strategy Health</span>
                    </div>
                    <div style={{ textAlign: 'center', padding: 'var(--space-4) 0' }}>
                        <div style={{
                            width: '100px',
                            height: '100px',
                            margin: '0 auto var(--space-3)',
                            borderRadius: '50%',
                            border: `6px solid ${getHealthColor(dashboardData?.health?.score || 0)}`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexDirection: 'column'
                        }}>
                            <span style={{
                                fontSize: 'var(--font-size-2xl)',
                                fontWeight: 700,
                                color: getHealthColor(dashboardData?.health?.score || 0)
                            }}>
                                {dashboardData?.health?.score || 0}
                            </span>
                            <span style={{ fontSize: '10px', color: 'var(--color-text-tertiary)' }}>/ 100</span>
                        </div>
                        <span style={{
                            fontSize: 'var(--font-size-sm)',
                            color: getHealthColor(dashboardData?.health?.score || 0),
                            fontWeight: 500,
                            textTransform: 'capitalize'
                        }}>
                            {dashboardData?.health?.status?.replace('_', ' ') || 'Unknown'}
                        </span>
                    </div>
                </div>

                {/* Funnel Health */}
                <div className="card" style={{ gridColumn: 'span 5' }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: 'var(--space-4)'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                            <BarChart3 size={18} style={{ color: 'var(--color-text-secondary)' }} />
                            <span style={{ fontSize: 'var(--font-size-sm)', fontWeight: 500 }}>Funnel Coverage</span>
                        </div>
                        <button
                            onClick={() => navigate('/funnel-builder')}
                            className="btn btn-ghost btn-sm"
                        >
                            View Funnel <ChevronRight size={14} />
                        </button>
                    </div>
                    <div style={{ display: 'flex', gap: 'var(--space-4)' }}>
                        {['TOFU', 'MOFU', 'BOFU'].map((stage) => {
                            const stageData = dashboardData?.funnelHealth?.stages?.[stage];
                            const pillarsCount = stageData?.pillars?.length || 0;
                            return (
                                <div
                                    key={stage}
                                    style={{
                                        flex: 1,
                                        padding: 'var(--space-3)',
                                        background: pillarsCount > 0 ? `var(--${stage === 'TOFU' ? 'info' : stage === 'MOFU' ? 'warning' : 'success'}-50)` : 'var(--color-bg-tertiary)',
                                        borderRadius: 'var(--radius-md)',
                                        textAlign: 'center'
                                    }}
                                >
                                    <span style={{
                                        fontSize: 'var(--font-size-xs)',
                                        color: 'var(--color-text-secondary)',
                                        display: 'block',
                                        marginBottom: 'var(--space-1)'
                                    }}>
                                        {stage}
                                    </span>
                                    <span style={{
                                        fontSize: 'var(--font-size-xl)',
                                        fontWeight: 700,
                                        color: pillarsCount > 0 ? `var(--${stage === 'TOFU' ? 'info' : stage === 'MOFU' ? 'warning' : 'success'}-700)` : 'var(--color-text-tertiary)'
                                    }}>
                                        {pillarsCount}
                                    </span>
                                    <span style={{
                                        fontSize: '10px',
                                        color: 'var(--color-text-tertiary)',
                                        display: 'block'
                                    }}>
                                        pillars
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Quick Actions */}
                <div className="card" style={{ gridColumn: 'span 4' }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--space-2)',
                        marginBottom: 'var(--space-4)'
                    }}>
                        <Sparkles size={18} style={{ color: 'var(--color-text-secondary)' }} />
                        <span style={{ fontSize: 'var(--font-size-sm)', fontWeight: 500 }}>Quick Actions</span>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                        <button
                            onClick={() => setShowBulkPlanner(true)}
                            className="btn btn-primary"
                            style={{ width: '100%', justifyContent: 'flex-start' }}
                        >
                            <FileText size={16} />
                            Plan Month's Content
                            <ArrowRight size={14} style={{ marginLeft: 'auto' }} />
                        </button>
                        <button
                            onClick={() => navigate('/creative-chat')}
                            className="btn btn-secondary"
                            style={{ width: '100%', justifyContent: 'flex-start' }}
                        >
                            <Sparkles size={16} />
                            Create Content
                            <ArrowRight size={14} style={{ marginLeft: 'auto' }} />
                        </button>
                        <button
                            onClick={() => navigate('/funnel-builder')}
                            className="btn btn-ghost"
                            style={{ width: '100%', justifyContent: 'flex-start' }}
                        >
                            <Target size={16} />
                            Build Funnel
                            <ArrowRight size={14} style={{ marginLeft: 'auto' }} />
                        </button>
                        <button
                            onClick={() => navigate('/schedule')}
                            className="btn btn-ghost"
                            style={{ width: '100%', justifyContent: 'flex-start' }}
                        >
                            <Calendar size={16} />
                            Schedule Posts
                            <ArrowRight size={14} style={{ marginLeft: 'auto' }} />
                        </button>
                    </div>
                </div>

                {/* Upcoming Promotions */}
                <PromotionsCard
                    promotions={promotions}
                    onCreatePromotion={handleCreatePromotion}
                    onEditPromotion={handleEditPromotion}
                    onDeletePromotion={handleDeletePromotion}
                    loading={promotionsLoading}
                />

                {/* Linked Products */}
                <ProductsCard
                    products={products}
                    onCreateProduct={handleCreateProduct}
                    onEditProduct={handleEditProduct}
                    onDeleteProduct={handleDeleteProduct}
                    onDiscoverProducts={handleDiscoverProducts}
                    onBulkEditVisualNotes={handleBulkEditVisualNotes}
                    loading={productsLoading}
                />

                {/* Opportunities & Gaps */}
                {dashboardData?.recommendations?.length > 0 && (
                    <div className="card" style={{ gridColumn: 'span 12' }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--space-2)',
                            marginBottom: 'var(--space-4)'
                        }}>
                            <Lightbulb size={18} style={{ color: 'var(--warning-600)' }} />
                            <span style={{ fontSize: 'var(--font-size-sm)', fontWeight: 500 }}>Opportunities & Gaps</span>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                            {dashboardData.recommendations.slice(0, 3).map((rec, idx) => (
                                <div
                                    key={idx}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        padding: 'var(--space-3)',
                                        background: rec.priority === 'high' ? 'var(--warning-50)' : 'var(--color-bg-secondary)',
                                        borderRadius: 'var(--radius-md)',
                                        borderLeft: `4px solid ${rec.priority === 'high' ? 'var(--warning-500)' : 'var(--info-500)'}`
                                    }}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                                        {rec.priority === 'high' ? (
                                            <AlertTriangle size={18} style={{ color: 'var(--warning-600)' }} />
                                        ) : (
                                            <Lightbulb size={18} style={{ color: 'var(--info-600)' }} />
                                        )}
                                        <span style={{ fontSize: 'var(--font-size-sm)' }}>{rec.message}</span>
                                    </div>
                                    <button className="btn btn-sm btn-ghost">
                                        {rec.action}
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Pillars Section */}
                <div style={{ gridColumn: 'span 12' }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: 'var(--space-4)'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                            <h2 style={{ margin: 0, fontSize: 'var(--font-size-lg)' }}>Your Pillars</h2>
                            {pillars.length > 0 && (
                                <span style={{
                                    fontSize: 'var(--font-size-xs)',
                                    color: (() => {
                                        const totalWeight = pillars.reduce((sum, p) => sum + (p.frequency?.currentWeight || 0), 0);
                                        return totalWeight === 100 ? 'var(--success-600)' : 'var(--warning-600)';
                                    })(),
                                    fontWeight: 500,
                                    padding: 'var(--space-1) var(--space-2)',
                                    background: (() => {
                                        const totalWeight = pillars.reduce((sum, p) => sum + (p.frequency?.currentWeight || 0), 0);
                                        return totalWeight === 100 ? 'var(--success-50)' : 'var(--warning-50)';
                                    })(),
                                    borderRadius: 'var(--radius-full)'
                                }}>
                                    {pillars.reduce((sum, p) => sum + (p.frequency?.currentWeight || 0), 0)}% total weight
                                </span>
                            )}
                        </div>
                        <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                            {pillars.length > 1 && (
                                <button
                                    onClick={() => setShowWeightsModal(true)}
                                    className="btn btn-ghost"
                                    title="Adjust content weight distribution across pillars"
                                >
                                    <SlidersHorizontal size={16} />
                                    Adjust Weights
                                </button>
                            )}
                            <button onClick={handleCreatePillar} className="btn btn-primary">
                                <Plus size={16} />
                                Add Pillar
                            </button>
                        </div>
                    </div>

                    {pillars.length === 0 ? (
                        <div className="card" style={{ textAlign: 'center', padding: 'var(--space-8)' }}>
                            <div style={{
                                width: '64px',
                                height: '64px',
                                margin: '0 auto var(--space-4)',
                                borderRadius: 'var(--radius-full)',
                                background: 'var(--color-bg-tertiary)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                <Compass size={32} style={{ color: 'var(--color-text-tertiary)' }} />
                            </div>
                            <h3 style={{ marginBottom: 'var(--space-2)' }}>No pillars defined yet</h3>
                            <p style={{ color: 'var(--color-text-secondary)', marginBottom: 'var(--space-4)', maxWidth: '400px', margin: '0 auto var(--space-4)' }}>
                                Marketing pillars are content themes that guide your strategy.
                                Define pillars like "Education", "Social Proof", or "Entertainment" to organize your content.
                            </p>
                            <button onClick={handleCreatePillar} className="btn btn-primary">
                                <Plus size={16} />
                                Create Your First Pillar
                            </button>
                        </div>
                    ) : (
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                            gap: 'var(--space-4)'
                        }}>
                            {pillars.map(pillar => (
                                <PillarCard
                                    key={pillar.id}
                                    pillar={pillar}
                                    onEdit={handleEditPillar}
                                    onDelete={handleDeletePillar}
                                    onArchive={handleArchivePillar}
                                    onDuplicate={handleDuplicatePillar}
                                    onClick={handleEditPillar}
                                    showMetrics={true}
                                />
                            ))}
                        </div>
                    )}

                    {/* Archived Pillars Section */}
                    {archivedPillars.length > 0 && (
                        <div style={{ marginTop: 'var(--space-4)' }}>
                            <button
                                onClick={() => setShowArchivedPillars(!showArchivedPillars)}
                                className="btn btn-ghost"
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 'var(--space-2)',
                                    fontSize: 'var(--font-size-sm)',
                                    color: 'var(--color-text-secondary)',
                                    padding: 'var(--space-2) var(--space-3)',
                                    marginBottom: 'var(--space-3)'
                                }}
                            >
                                <Archive size={16} />
                                Archived Pillars ({archivedPillars.length})
                                {showArchivedPillars ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                            </button>

                            {showArchivedPillars && (
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                                    gap: 'var(--space-4)'
                                }}>
                                    {archivedPillars.map(pillar => (
                                        <PillarCard
                                            key={pillar.id}
                                            pillar={pillar}
                                            onEdit={handleEditPillar}
                                            onDelete={handleDeletePillar}
                                            onArchive={handleArchivePillar}
                                            onClick={handleEditPillar}
                                            showMetrics={false}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Pillar Editor Modal */}
            <PillarEditor
                isOpen={showPillarEditor}
                onClose={() => {
                    setShowPillarEditor(false);
                    setEditingPillar(null);
                }}
                onSave={handleSavePillar}
                pillar={editingPillar}
                brand={selectedBrand}
                loading={savingPillar}
            />

            {/* Pillar Weights Modal */}
            <PillarWeightsModal
                isOpen={showWeightsModal}
                onClose={() => setShowWeightsModal(false)}
                onSave={handleSaveWeights}
                pillars={pillars}
                loading={savingWeights}
            />

            {/* Delete Confirmation */}
            <ConfirmationModal
                isOpen={showDeleteConfirm}
                onClose={() => {
                    setShowDeleteConfirm(false);
                    setPillarToDelete(null);
                }}
                onConfirm={confirmDeletePillar}
                title="Delete Pillar?"
                message={`Are you sure you want to delete "${pillarToDelete?.name}"? This action cannot be undone.`}
                confirmText="Delete"
                variant="danger"
            />

            {/* Goals Modal */}
            {showGoalsModal && (
                <div
                    style={{
                        position: 'fixed',
                        inset: 0,
                        background: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 9999
                    }}
                    onClick={(e) => e.target === e.currentTarget && setShowGoalsModal(false)}
                >
                    <div className="card" style={{ width: '100%', maxWidth: '500px', margin: 'var(--space-4)' }}>
                        <h3 style={{ marginBottom: 'var(--space-4)' }}>Business Goals</h3>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                            <div>
                                <label style={{ display: 'block', fontSize: 'var(--font-size-sm)', marginBottom: 'var(--space-1)' }}>
                                    Monthly Revenue Target
                                </label>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                                    <span>$</span>
                                    <input
                                        type="number"
                                        value={goalsForm.monthlyRevenueTarget}
                                        onChange={(e) => setGoalsForm(prev => ({ ...prev, monthlyRevenueTarget: parseInt(e.target.value) || 0 }))}
                                        className="input"
                                        style={{ flex: 1 }}
                                    />
                                </div>
                            </div>
                            <div>
                                <label style={{ display: 'block', fontSize: 'var(--font-size-sm)', marginBottom: 'var(--space-1)' }}>
                                    Average Order Value
                                </label>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                                    <span>$</span>
                                    <input
                                        type="number"
                                        value={goalsForm.averageOrderValue}
                                        onChange={(e) => setGoalsForm(prev => ({ ...prev, averageOrderValue: parseInt(e.target.value) || 0 }))}
                                        className="input"
                                        style={{ flex: 1 }}
                                    />
                                </div>
                            </div>
                            <div>
                                <label style={{ display: 'block', fontSize: 'var(--font-size-sm)', marginBottom: 'var(--space-1)' }}>
                                    Customer Lifetime Value (optional)
                                </label>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                                    <span>$</span>
                                    <input
                                        type="number"
                                        value={goalsForm.customerLifetimeValue}
                                        onChange={(e) => setGoalsForm(prev => ({ ...prev, customerLifetimeValue: parseInt(e.target.value) || 0 }))}
                                        className="input"
                                        style={{ flex: 1 }}
                                    />
                                </div>
                            </div>
                            <div>
                                <label style={{ display: 'block', fontSize: 'var(--font-size-sm)', marginBottom: 'var(--space-1)' }}>
                                    Current Challenge
                                </label>
                                <select
                                    value={goalsForm.currentChallenge}
                                    onChange={(e) => setGoalsForm(prev => ({ ...prev, currentChallenge: e.target.value }))}
                                    className="input"
                                    style={{ width: '100%' }}
                                >
                                    <option value="new_customers">Acquiring New Customers</option>
                                    <option value="repeat_purchases">Driving Repeat Purchases</option>
                                    <option value="brand_building">Building Brand Awareness</option>
                                    <option value="market_expansion">Expanding to New Markets</option>
                                </select>
                            </div>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-2)', marginTop: 'var(--space-4)' }}>
                            <button onClick={() => setShowGoalsModal(false)} className="btn btn-ghost">Cancel</button>
                            <button onClick={handleSaveGoals} className="btn btn-primary">Save Goals</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Strategy Setup Wizard */}
            <StrategySetupWizard
                isOpen={showSetupWizard}
                onClose={() => setShowSetupWizard(false)}
                onComplete={() => {
                    setShowSetupWizard(false);
                    loadDashboardData();
                }}
            />

            {/* Bulk Content Planner */}
            <BulkContentPlanner
                isOpen={showBulkPlanner}
                onClose={() => {
                    setShowBulkPlanner(false);
                    setResumePlanId(null);
                    // Refresh incomplete plans in case progress was made
                    loadIncompletePlans();
                }}
                onComplete={(result) => {
                    setShowBulkPlanner(false);
                    setResumePlanId(null);
                    // Refresh incomplete plans list
                    loadIncompletePlans();
                    // Navigate to scheduler with the scheduled briefs
                    if (result?.briefs?.length > 0) {
                        navigate('/schedule');
                    }
                }}
                resumePlanId={resumePlanId}
            />

            {/* Promotion Editor Modal */}
            <PromotionEditorModal
                isOpen={showPromotionModal}
                onClose={() => {
                    setShowPromotionModal(false);
                    setEditingPromotion(null);
                }}
                onSave={handleSavePromotion}
                promotion={editingPromotion}
                loading={savingPromotion}
                products={products}
            />

            {/* Delete Promotion Confirmation */}
            <ConfirmationModal
                isOpen={showDeletePromotionConfirm}
                onClose={() => {
                    setShowDeletePromotionConfirm(false);
                    setPromotionToDelete(null);
                }}
                onConfirm={confirmDeletePromotion}
                title="Delete Promotion?"
                message={`Are you sure you want to delete "${promotionToDelete?.name}"? This action cannot be undone.`}
                confirmText="Delete"
                variant="danger"
            />

            {/* Product Editor Modal */}
            <ProductEditorModal
                isOpen={showProductModal}
                onClose={() => {
                    setShowProductModal(false);
                    setEditingProduct(null);
                }}
                onSave={handleSaveProduct}
                product={editingProduct}
                loading={savingProduct}
            />

            {/* Discover Products Modal */}
            <DiscoverProductsModal
                isOpen={showDiscoverModal}
                onClose={() => setShowDiscoverModal(false)}
                onImport={handleImportProducts}
                websiteUrl={selectedBrand?.website || ''}
            />

            {/* Delete Product Confirmation */}
            <ConfirmationModal
                isOpen={showDeleteProductConfirm}
                onClose={() => {
                    setShowDeleteProductConfirm(false);
                    setProductToDelete(null);
                }}
                onConfirm={confirmDeleteProduct}
                title="Delete Product?"
                message={`Are you sure you want to delete "${productToDelete?.name}"? This action cannot be undone.`}
                confirmText="Delete"
                variant="danger"
            />

            {/* Delete Content Plan Confirmation - Custom Modal with Library Items Warning */}
            {showDeletePlanConfirm && (
                <div
                    onClick={(e) => {
                        if (e.target === e.currentTarget && !deletingPlan) {
                            setShowDeletePlanConfirm(false);
                            setPlanToDelete(null);
                            setDeletePlanError(null);
                        }
                    }}
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 9999,
                        padding: 'var(--space-4)'
                    }}
                >
                    <div
                        style={{
                            backgroundColor: 'var(--color-surface)',
                            borderRadius: 'var(--radius-lg)',
                            padding: 'var(--space-6)',
                            maxWidth: '500px',
                            width: '100%',
                            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
                        }}
                    >
                        <h2 style={{
                            margin: '0 0 var(--space-3) 0',
                            fontSize: '1.5rem',
                            fontWeight: 600,
                            color: 'var(--color-text-primary)'
                        }}>
                            Delete Content Plan?
                        </h2>

                        {/* Error message */}
                        {deletePlanError && (
                            <div style={{
                                padding: 'var(--space-3)',
                                background: 'var(--error-50)',
                                borderRadius: 'var(--radius-md)',
                                border: '1px solid var(--error-200)',
                                marginBottom: 'var(--space-4)',
                                color: 'var(--error-700)',
                                fontSize: 'var(--font-size-sm)'
                            }}>
                                {deletePlanError}
                            </div>
                        )}

                        {/* Scheduled posts warning - blocking */}
                        {planScheduledPostsCount > 0 && (
                            <div style={{
                                padding: 'var(--space-3)',
                                background: 'var(--warning-50)',
                                borderRadius: 'var(--radius-md)',
                                border: '1px solid var(--warning-200)',
                                marginBottom: 'var(--space-4)',
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: 'var(--space-2)'
                            }}>
                                <AlertTriangle size={20} style={{ color: 'var(--warning-600)', flexShrink: 0, marginTop: '2px' }} />
                                <div>
                                    <div style={{ fontWeight: 600, color: 'var(--warning-700)', marginBottom: '4px' }}>
                                        Cannot Delete - Scheduled Posts Exist
                                    </div>
                                    <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--warning-600)' }}>
                                        This plan has {planScheduledPostsCount} post(s) that are still scheduled.
                                        Please cancel or publish them before deleting the plan.
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Main message */}
                        {planScheduledPostsCount === 0 && (
                            <p style={{
                                margin: '0 0 var(--space-4) 0',
                                fontSize: '1rem',
                                color: 'var(--color-text-secondary)',
                                lineHeight: '1.5'
                            }}>
                                Are you sure you want to delete "{planToDelete?.name || 'this content plan'}"?
                                All briefs in this plan will also be deleted. This action cannot be undone.
                            </p>
                        )}

                        {/* Library items warning and checkbox */}
                        {planLibraryItemsCount > 0 && planScheduledPostsCount === 0 && (
                            <div style={{
                                padding: 'var(--space-3)',
                                background: 'var(--error-50)',
                                borderRadius: 'var(--radius-md)',
                                border: '1px solid var(--error-200)',
                                marginBottom: 'var(--space-4)'
                            }}>
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: 'var(--space-2)',
                                    marginBottom: 'var(--space-3)'
                                }}>
                                    <AlertTriangle size={20} style={{ color: 'var(--error-600)', flexShrink: 0, marginTop: '2px' }} />
                                    <div>
                                        <div style={{ fontWeight: 600, color: 'var(--error-700)', marginBottom: '4px' }}>
                                            {planLibraryItemsCount} Creative{planLibraryItemsCount !== 1 ? 's' : ''} Associated
                                        </div>
                                        <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--error-600)' }}>
                                            This plan has generated creatives saved in your library.
                                        </div>
                                    </div>
                                </div>
                                <label style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 'var(--space-2)',
                                    cursor: 'pointer',
                                    fontSize: 'var(--font-size-sm)',
                                    color: 'var(--color-text-primary)'
                                }}>
                                    <input
                                        type="checkbox"
                                        checked={deleteLibraryItems}
                                        onChange={(e) => setDeleteLibraryItems(e.target.checked)}
                                        style={{ width: '16px', height: '16px' }}
                                    />
                                    Also delete {planLibraryItemsCount} creative{planLibraryItemsCount !== 1 ? 's' : ''} from library
                                </label>
                            </div>
                        )}

                        {/* Actions */}
                        <div style={{
                            display: 'flex',
                            gap: 'var(--space-3)',
                            justifyContent: 'flex-end'
                        }}>
                            <button
                                onClick={() => {
                                    if (!deletingPlan) {
                                        setShowDeletePlanConfirm(false);
                                        setPlanToDelete(null);
                                        setDeletePlanError(null);
                                    }
                                }}
                                disabled={deletingPlan}
                                className="btn btn-ghost"
                            >
                                Cancel
                            </button>
                            {planScheduledPostsCount === 0 && (
                                <button
                                    onClick={confirmDeletePlan}
                                    disabled={deletingPlan}
                                    className="btn"
                                    style={{
                                        backgroundColor: 'var(--error-500)',
                                        color: 'white',
                                        opacity: deletingPlan ? 0.7 : 1
                                    }}
                                >
                                    {deletingPlan ? (
                                        <>
                                            <Loader size={16} className="spin" />
                                            Deleting...
                                        </>
                                    ) : (
                                        'Delete Plan'
                                    )}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Bulk Edit Visual Notes Modal */}
            <BulkEditVisualNotesModal
                isOpen={showBulkEditVisualNotes}
                onClose={() => {
                    setShowBulkEditVisualNotes(false);
                    setBulkEditProducts([]);
                }}
                onSave={handleSaveBulkVisualNotes}
                products={bulkEditProducts}
                loading={savingBulkEdit}
            />
        </div>
    );
};

export default StrategyDashboard;
